import { UIButton, UIDiv, UISpan, UIText } from '@ui';

export class AICommandsPlugin {
  static openLists = new Set();

  constructor(textElement, options = {}) {
    this.textElement = textElement;
    this.commandListContainer = null;
    this.trigger = options.trigger || '/';
    this.isVisible = false;
    this.currentFilteredCommands = []; // Track filtered commands
    this.onLocalCommandResponse = options.onLocalCommandResponse || null; // Callback for local command responses

    // Define available AI commands
    this.aiCommands = [
      {
        id: 'help',
        command: '/help',
        description: 'Show example prompt',
        category: 'General',
        icon: '❓',
        local: true, // Handled locally
      },
      {
        id: 'tools',
        command: '/tools',
        description: 'Show available tools',
        category: 'General',
        icon: '⚙️',
        local: true, // Handled locally
      },
      // {
      //   id: 'create-report',
      //   command: '/report',
      //   description: 'Create a report of a given task',
      //   category: 'Analysis',
      //   icon: '📝',
      //   local: false, // Requires backend
      // },
    ];

    this.init();
  }

  init() {
    // Bind event handlers
    this.textElement.onInput(() => this.#handleCommandSuggestion());
    this.textElement.onKeyDown(e => this.#handleKeyDown(e));
  }

  #handleKeyDown(e) {
    if (this.commandListContainer && this.isVisible && e.key === 'Escape') {
      this.#hideCommandList();
      e.preventDefault();
      return;
    }

    // Handle Enter, Tab, or Space to auto-select first command from filtered list
    if (
      (e.key === 'Enter' || e.key === 'Tab' || e.key === ' ') &&
      this.isVisible
    ) {
      e.preventDefault();

      // Get the first command from the current filtered list
      if (this.currentFilteredCommands.length > 0) {
        const firstCommand = this.currentFilteredCommands[0];
        this.#hideCommandList();
        this.#replaceTextWithCommand(firstCommand);
        return;
      }
    }
  }

  #handleCommandSuggestion() {
    const selection = window.getSelection();
    if (!selection.rangeCount) {
      this.#hideCommandList();
      return;
    }

    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;
    const cursorPosition = range.startOffset;

    if (
      !this.textElement.dom.contains(textNode) ||
      textNode.nodeType !== Node.TEXT_NODE
    ) {
      this.#hideCommandList();
      return;
    }

    // Check if there's any text content before the current position in the entire input
    const fullText = this.textElement.getValue().trim();
    const textContent = textNode.textContent;
    const triggerIndex = textContent.lastIndexOf(this.trigger, cursorPosition);

    // Only allow commands if:
    // 1. The trigger is found in the current text node
    // 2. The trigger is at the start of the text node (triggerIndex === 0)
    // 3. There's no other text content before this text node in the entire input
    if (
      triggerIndex === 0 &&
      (fullText === '' || fullText.startsWith(this.trigger))
    ) {
      const query = textContent.substring(triggerIndex + 1, cursorPosition);

      // If query contains a space, hide the list
      if (query.includes(' ')) {
        this.#hideCommandList();
        return;
      }

      this.#showCommandList();
      this.#filterCommandList(query);
    } else {
      this.#hideCommandList();
    }
  }

  #showCommandList() {
    if (!this.commandListContainer) {
      this.#createCommandListContainer();
    }

    if (this.isVisible) return;

    // Close other open suggestion lists
    AICommandsPlugin.openLists.forEach(plugin => {
      if (plugin !== this) {
        plugin.#hideCommandList();
      }
    });

    this.isVisible = true;
    this.commandListContainer.removeClass('hidden');
    AICommandsPlugin.openLists.add(this);

    document.removeEventListener('click', this.onDocumentClickEvent);
    requestAnimationFrame(() => {
      document.addEventListener('click', this.onDocumentClickEvent);
    });

    this.#buildAndPopulateCommandList();
  }

  #hideCommandList() {
    if (!this.isVisible) return;
    this.isVisible = false;
    if (this.commandListContainer) {
      this.commandListContainer.addClass('hidden');
    }
    AICommandsPlugin.openLists.delete(this);
    document.removeEventListener('click', this.onDocumentClickEvent);
  }

  #createCommandListContainer() {
    this.commandListContainer = new UIDiv();
    this.commandListContainer.setClass(
      'hidden bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-y-auto z-50',
    );

    this.commandListContainer.dom.style.position = 'absolute';
    this.commandListContainer.dom.style.bottom = '100%';
    this.commandListContainer.dom.style.left = '0';
    this.commandListContainer.dom.style.right = '0';
    this.commandListContainer.dom.style.zIndex = '1000';
    this.commandListContainer.dom.style.marginBottom = '5px';

    const container =
      this.textElement.dom.closest('.relative') ||
      this.textElement.dom.parentElement;
    if (container) {
      container.appendChild(this.commandListContainer.dom);
    }
  }

  #buildAndPopulateCommandList(filterQuery = '') {
    if (!this.commandListContainer) return;

    this.commandListContainer.clear();

    const lowerCaseQuery = filterQuery.toLowerCase();
    const filteredCommands = this.aiCommands.filter(
      command =>
        command.command.toLowerCase().includes(lowerCaseQuery) ||
        command.description.toLowerCase().includes(lowerCaseQuery) ||
        command.category.toLowerCase().includes(lowerCaseQuery),
    );

    // Store the current filtered commands for key navigation
    this.currentFilteredCommands = filteredCommands;

    if (filteredCommands.length === 0) {
      this.currentFilteredCommands = [];
      const noResultsMessage = new UIDiv();
      noResultsMessage.setClass('p-4 text-gray-500 text-sm text-center');
      noResultsMessage.setTextContent('No matching commands found');
      this.commandListContainer.add(noResultsMessage);
      return;
    }

    // Group commands by category
    const groupedCommands = filteredCommands.reduce((groups, command) => {
      const category = command.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(command);
      return groups;
    }, {});

    // Render grouped commands
    Object.keys(groupedCommands).forEach((category, categoryIndex) => {
      // Add category header if there are multiple categories
      if (Object.keys(groupedCommands).length > 1) {
        const categoryHeader = new UIDiv();
        categoryHeader.setClass(
          'px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-50 border-b border-gray-100',
        );
        categoryHeader.setTextContent(category);
        this.commandListContainer.add(categoryHeader);
      }

      groupedCommands[category].forEach(command => {
        const commandContainer = new UIButton();
        commandContainer.onClick(event =>
          this.onCommandClickEvent(command, event),
        );
        commandContainer.setClass(
          'flex w-full items-center p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0',
        );

        // Command icon
        const commandIcon = new UISpan(command.icon);
        commandIcon.setClass('w-6 h-6 mr-3 flex-shrink-0 text-center text-lg');
        commandContainer.add(commandIcon);

        const infoContainer = new UIDiv();
        infoContainer.setClass('flex-1 text-left');

        const commandName = new UIText(command.command);
        commandName.setClass(
          'text-sm font-medium text-blue-600 block truncate',
        );

        const commandDescription = new UIText(command.description);
        commandDescription.setClass(
          'text-xs text-gray-500 block truncate mt-1',
        );

        infoContainer.add(commandName);
        infoContainer.add(commandDescription);

        commandContainer.add(infoContainer);
        this.commandListContainer.add(commandContainer);
      });
    });
  }

  #filterCommandList(query) {
    this.#buildAndPopulateCommandList(query);
  }

  onCommandClickEvent(command, event) {
    event.stopPropagation();
    this.#hideCommandList();
    this.#insertCommandReference(command);
    this.textElement.dom.focus();
  }

  onDocumentClickEvent = (event => {
    if (
      !this.commandListContainer?.contains(event.target) &&
      (!this.textElement.dom || !this.textElement.dom.contains(event.target))
    ) {
      this.#hideCommandList();
    }
  }).bind(this);

  #insertCommandReference(command) {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const originalRange = selection.getRangeAt(0);
    const textNode = originalRange.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) return;

    const cursorPosition = originalRange.startOffset;
    const textContent = textNode.textContent;
    const triggerIndex = textContent.lastIndexOf(this.trigger, cursorPosition);

    if (triggerIndex === -1) return;

    const commandSpan = new UISpan(`${command.command}`);
    commandSpan.dataset('commandId', command.id);
    commandSpan.dataset('commandName', command.command);
    commandSpan.dataset('commandDescription', command.description);

    commandSpan.dom.contentEditable = 'false';
    commandSpan.addClass(
      'ai-command-reference bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium cursor-pointer',
    );

    const replaceRange = document.createRange();
    replaceRange.setStart(textNode, triggerIndex);
    replaceRange.setEnd(textNode, cursorPosition);
    replaceRange.deleteContents();

    const spaceNode = document.createTextNode('\u00A0');
    replaceRange.insertNode(spaceNode);
    replaceRange.insertNode(commandSpan.dom);

    originalRange.setStartAfter(spaceNode);
    originalRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(originalRange);

    this.textElement.dom.focus();
  }

  #replaceTextWithCommand(command) {
    // Clear the entire text content and replace with command reference
    this.textElement.setValue('');

    // Create the command span
    const commandSpan = new UISpan(`${command.command}`);
    commandSpan.dataset('commandId', command.id);
    commandSpan.dataset('commandName', command.command);
    commandSpan.dataset('commandDescription', command.description);

    commandSpan.dom.contentEditable = 'false';
    commandSpan.addClass(
      'ai-command-reference bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium cursor-pointer',
    );

    // Insert the command span at the beginning
    const textElement = this.textElement.dom;
    textElement.appendChild(commandSpan.dom);

    // Add a space after the command
    const spaceNode = document.createTextNode('\u00A0');
    textElement.appendChild(spaceNode);

    // Set cursor position after the command
    const selection = window.getSelection();
    const range = document.createRange();
    range.setStartAfter(spaceNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    this.textElement.dom.focus();
  }

  handleLocalCommand(command) {
    switch (command.id) {
      case 'help':
        return this.#getHelpText();
      case 'tools':
        return this.#getToolsText();
      default:
        return null;
    }
  }

  #getHelpText() {
    return [
      '## Example Prompts',
      '',
      '- **Create a new project:**',
      '  `Create a new project called Factory Retrofit with deadline December 15.`',
      '',
      '- **Add a task:**',
      '  `Add a task Render 3D Scan under Factory Retrofit due next Monday.`',
      '',
      '- **Assign a task:**',
      '  `Assign Budget Approval to Maria.`',
      '',
      '- **Update a deadline:**',
      '  `Update the deadline for Render 3D Scan to November 1.`',
      '',
      '- **Mark as completed:**',
      '  `Mark Electrical Blueprint Check as completed.`',
      // '',
      // '- **Generate reports:**',
      // '  `/report Create a progress report for task TC-01`',
      // '  `/report Generate a status summary for Factory Retrofit project`',
      '',
      '- **Summarize files:**',
      '  `Give me a summary of the attached files.`',
      '',
      '- **Project health report:**',
      '  `Give me a project health report for Factory Retrofit.`',
      '',
      '- **Summarize comments:**',
      '  `Summarize all comments on tasks tagged Urgent in Project Alpha.`',
      '',
      // '---',
      // '',
      // '### Tips',
      // '- Use `#` to reference tasks/projects',
      // '- Use `@` to mention a team member',
      // '- Use `/` for AI commands (like `/tools`)',
    ].join('\n');
  }

  #getToolsText() {
    let toolsText = 'Available Project Management Functions:\n\n';

    // Project Tools
    toolsText += '📁 PROJECT MANAGEMENT:\n';
    toolsText += '• Create new projects\n';
    toolsText += '• Update existing projects\n';
    toolsText += '• Delete projects\n';
    toolsText += '• Retrieve project information\n\n';

    // Task Tools
    toolsText += '📄 TASK MANAGEMENT:\n';
    toolsText += '• Create new tasks\n';
    toolsText += '• Update task details\n';
    toolsText += '• Delete tasks\n';
    toolsText += '• Retrieve task information\n';
    toolsText += '• Generate task reports (using `/report` command)\n\n';

    // Column Tools
    toolsText += '📋 COLUMN MANAGEMENT:\n';
    toolsText += '• Create columns\n';
    toolsText += '• Delete columns\n\n';

    toolsText += '📂 FILE ANALYSIS:\n';
    toolsText += '• Summarize file contents\n';
    toolsText += '• Extract data from files\n';
    toolsText += '• Generate file reports\n';
    toolsText += '• Perform comparative analysis on files\n\n';

    return toolsText;
  }

  getAICommands() {
    return this.aiCommands;
  }

  destroy() {
    this.#hideCommandList();
    if (this.commandListContainer && this.commandListContainer.dom.parentNode) {
      this.commandListContainer.dom.parentNode.removeChild(
        this.commandListContainer.dom,
      );
    }
  }
}
